import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";
import { useEffect, useRef } from "react";
import { BRANDS, COMMODITIES, COMPANY, SLIDERS } from "@/lib/brands";
import { Reveal, RevealWords } from "@/components/site/Reveal";
import Slider from "@/components/site/Slider";
import useSEO from "@/lib/useSEO";
import { ExternalLink } from "lucide-react";

const TESTIMONIALS = [
  {
    body: "Reached out urgently for Alfalfa hay for my rabbits. Charges, shipping and quantity advice were spot on. Ordered evening, arrived next morning — clean and dust-free.",
    name: "Amit Natekar",
    role: "Pet parent · Pune",
  },
  {
    body: "Awesome quality of hay, packed and delivered beautifully. My baby guinea pigs love the Alfalfa. RSA is now my default supplier.",
    name: "Ishita Gupta",
    role: "Small pet owner",
  },
  {
    body: "Visited the Kamshet warehouse personally. Bought both Alfalfa and Timothy. The owner was extremely helpful and the quality is consistently excellent.",
    name: "Vikas Bhosale",
    role: "Rabbit breeder",
  },
  {
    body: "Professionalism, quality, responsiveness, value — every box ticked. Behaviour and response of the seller is exceptionally generous.",
    name: "Anushri Bhattacharyya",
    role: "Verified buyer",
  },
];

export default function Home() {
  useSEO({
    title:
      "RSA Overseas Private Limited — Importer of Seeds & Hay · Manufacturer of Animal Feeds",
    description:
      "Importer of sunflower seeds, raw oats and premium hay (Timothy, Alfalfa, Meadow, Orchard). Manufacturer of animal feeds under EquiOne, Hopsy, AviPure & NutriMax. Est. 2018 · Kamshet, Pune.",
    path: "/",
  });
  const heroRef = useRef(null);
  const portraitRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const heroImageY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const heroImageScale = useTransform(scrollYProgress, [0, 1], [1, 1.08]);

  // Mouse parallax — subtle 3D tilt on the hero portrait
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [6, -6]), { stiffness: 120, damping: 20 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-8, 8]), { stiffness: 120, damping: 20 });
  const layerX = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 20 });
  const layerY = useSpring(useTransform(my, [-0.5, 0.5], [-10, 10]), { stiffness: 120, damping: 20 });

  useEffect(() => {
    const el = portraitRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      mx.set((e.clientX - r.left) / r.width - 0.5);
      my.set((e.clientY - r.top) / r.height - 0.5);
    };
    const onLeave = () => {
      mx.set(0);
      my.set(0);
    };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mousemove", onMove);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, [mx, my]);

  return (
    <div data-testid="home-page" className="bg-parchment">
      {/* ————————————————————————————————
          HERO — Dark aubergine · asymmetric composition
         ———————————————————————————————— */}
      <section
        ref={heroRef}
        className="relative bg-aubergine text-parchment overflow-hidden"
      >
        <motion.div
          aria-hidden
          className="absolute inset-0 opacity-90"
          style={{
            backgroundImage:
              "radial-gradient(1200px 600px at 90% 10%, rgba(225,29,96,0.32), transparent 55%), radial-gradient(900px 500px at -10% 100%, rgba(147,51,234,0.24), transparent 55%)",
          }}
        />
        <div aria-hidden className="absolute inset-0 grain-overlay opacity-100" />

        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 pt-16 md:pt-20 pb-24 md:pb-32">
          {/* Eyebrow row */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="flex items-center justify-between text-[10px] tracking-[0.32em] uppercase font-semibold text-parchment/60 mb-16 md:mb-20"
          >
            <div className="flex items-center gap-4">
              <span className="h-px w-10 bg-parchment/40" />
              A House of Seeds, Hay & Animal Feeds
            </div>
            <div className="hidden md:block">Est. {COMPANY.est} · Kamshet, Pune</div>
          </motion.div>

          <div className="grid lg:grid-cols-12 gap-x-14 gap-y-14 items-start">
            <div className="lg:col-span-7 order-2 lg:order-1">
              <h1
                data-testid="hero-heading"
                className="font-display font-light text-[52px] sm:text-[76px] md:text-[104px] lg:text-[128px] xl:text-[152px] tracking-[-0.045em] leading-[0.88] text-parchment text-balance"
              >
                <RevealWords
                  text="Honest"
                  delay={0.15}
                />
                <br />
                <span className="italic font-normal">
                  <RevealWords text="nutrition," delay={0.35} />
                </span>
                <br />
                <RevealWords text="quietly" delay={0.55} />{" "}
                <span className="italic font-normal text-magenta">
                  <RevealWords text="perfected." delay={0.75} />
                </span>
              </h1>

              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.1, ease: [0.22, 1, 0.36, 1] }}
                className="mt-12 font-serif italic text-lg md:text-xl lg:text-2xl text-parchment/85 leading-[1.5] max-w-2xl"
              >
                RSA Overseas is an{" "}
                <span className="text-magenta not-italic font-sans font-medium">
                  importer of Seeds & Hay
                </span>{" "}
                and a{" "}
                <span className="text-magenta not-italic font-sans font-medium">
                  manufacturer of Animal Feeds
                </span>{" "}
                — specialising in sunflower seeds, raw oats and four hay
                varieties: Timothy, Alfalfa, Meadow and Orchard.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
                className="mt-12 flex flex-wrap items-center gap-6"
              >
                <Link
                  to="/brands"
                  data-testid="hero-explore-brands"
                  className="group inline-flex items-center gap-3 bg-magenta hover:bg-parchment hover:text-aubergine text-parchment text-[11px] tracking-[0.28em] uppercase font-semibold px-8 py-5 transition-all duration-300"
                >
                  Explore Our Brands
                  <span className="h-px w-6 bg-current transition-all group-hover:w-10" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-contact-cta"
                  className="text-[11px] tracking-[0.28em] uppercase font-semibold text-parchment/80 hover:text-magenta transition-colors border-b border-parchment/40 pb-1"
                >
                  Request a Quote
                </Link>
              </motion.div>
            </div>

            {/* Arched portrait image with parallax + mouse tilt */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className="lg:col-span-5 order-1 lg:order-2 lg:pt-6"
              style={{ perspective: 1200 }}
            >
              <motion.div
                ref={portraitRef}
                className="relative mx-auto w-full max-w-md lg:max-w-none lg:ml-auto"
                style={{ rotateX: rotX, rotateY: rotY, transformStyle: "preserve-3d" }}
              >
                <div
                  className="relative aspect-[3/4] overflow-hidden"
                  style={{
                    borderTopLeftRadius: "50% 25%",
                    borderTopRightRadius: "50% 25%",
                  }}
                >
                  <motion.img
                    src="https://equione.in/brand/hero_real_1.png"
                    alt="Premium equine nutrition — RSA Overseas"
                    className="absolute inset-0 h-full w-full object-cover"
                    style={{ y: heroImageY, scale: heroImageScale, x: layerX }}
                    draggable={false}
                  />
                  <div
                    aria-hidden
                    className="absolute inset-0 bg-gradient-to-t from-aubergine/50 via-transparent to-transparent"
                  />
                  {/* Floating chapter marker (subtle 3D depth cue) */}
                  <motion.div
                    aria-hidden
                    className="absolute top-6 right-6 flex flex-col items-end gap-1"
                    style={{ x: layerX, y: layerY, translateZ: 40 }}
                  >
                    <span className="font-display italic text-parchment/90 text-2xl leading-none">
                      №01
                    </span>
                    <span className="text-[9px] tracking-[0.32em] uppercase text-parchment/70 font-semibold">
                      Kamshet · IND
                    </span>
                  </motion.div>
                </div>
                <motion.div
                  className="absolute -bottom-4 -left-4 md:-left-8 bg-parchment text-aubergine px-5 py-4 shadow-[0_18px_40px_-10px_rgba(0,0,0,0.4)] max-w-[240px]"
                  style={{ x: layerX, y: layerY, translateZ: 60 }}
                >
                  <div className="text-[9px] tracking-[0.32em] uppercase font-semibold text-magenta">
                    Est. 2018 · Since day one
                  </div>
                  <div className="mt-2 font-display italic text-lg leading-tight">
                    Grain from farmer to feed room.
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          </div>

          {/* Bottom stat row */}
          <Reveal>
            <div className="mt-24 md:mt-32 grid grid-cols-2 md:grid-cols-4 gap-y-10 border-t border-parchment/15 pt-14">
              {[
                { v: COMPANY.est, l: "Established" },
                { v: COMPANY.trustedBy, l: "Clients Served" },
                { v: "06", l: "Core Commodities" },
                { v: "Global", l: "Import & Export" },
              ].map((s, i) => (
                <Reveal key={s.l} delay={0.1 + i * 0.08}>
                  <div className="font-display font-light text-5xl md:text-6xl lg:text-7xl text-parchment tracking-[-0.03em]">
                    {s.v}
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-[0.28em] text-parchment/50 font-semibold">
                    {s.l}
                  </div>
                </Reveal>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————————————————————
          MARQUEE — commodity names
         ———————————————————————————————— */}
      <section className="bg-parchment border-y border-aubergine/10 py-8 overflow-hidden">
        <div className="marquee-track gap-16 items-center text-aubergine">
          {[...Array(3)].flatMap((_, r) =>
            [
              "Raw Oats",
              "Sunflower Seeds",
              "Timothy Hay",
              "Alfalfa",
              "Meadow Hay",
              "Orchard Hay",
              "Est. 2018 · Pune",
            ].map((t, i) => (
              <span
                key={`${r}-${i}`}
                className="font-display italic font-light text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] flex items-center gap-16 whitespace-nowrap"
              >
                {t}
                <span className="text-magenta text-2xl not-italic">❋</span>
              </span>
            )),
          )}
        </div>
      </section>

      {/* ————————————————————————————————
          COMMODITIES — Grains, Seeds & Hay portfolio
         ———————————————————————————————— */}
      <section
        data-testid="home-commodities-section"
        className="relative bg-parchment"
      >
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-36">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-y-10 items-end mb-16 md:mb-20">
              <div className="md:col-span-8">
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4">
                  <span className="h-px w-8 bg-magenta" />
                  Grains, Seeds & Hay
                </div>
                <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl mt-8 tracking-[-0.035em] leading-[0.94] text-aubergine text-balance">
                  Six commodities.{" "}
                  <span className="italic text-magenta">One insistence</span>{" "}
                  on quality.
                </h2>
              </div>
              <div className="md:col-span-4 md:pl-6">
                <p className="text-ink/70 leading-relaxed">
                  We import the seeds and hay that ground our recipes, and
                  manufacture the animal feeds those recipes become —
                  hand-graded, moisture-tested and packed with the kind of
                  care that used to be the norm.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-20">
            {COMMODITIES.map((c, i) => (
              <Reveal key={c.name} delay={(i % 3) * 0.1}>
                <CommodityCard commodity={c} idx={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————————————————————————————————
          SLIDER · GLOBAL IMPORT — containers, ports, bales
         ———————————————————————————————— */}
      <Slider id="import" {...SLIDERS.import} />

      {/* ————————————————————————————————
          MANIFESTO — Numbered editorial chapters
         ———————————————————————————————— */}
      <section className="relative bg-aubergine text-parchment overflow-hidden grain-overlay">
        <div
          aria-hidden
          className="absolute top-0 left-1/2 -translate-x-1/2 h-full w-px bg-parchment/10 hidden md:block"
        />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-36">
          <div className="grid lg:grid-cols-12 gap-x-16 gap-y-14 mb-20 md:mb-28">
            <div className="lg:col-span-5">
              <Reveal>
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4">
                  <span className="h-px w-8 bg-magenta" />
                  A Manifesto in Four Chapters
                </div>
                <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl mt-8 tracking-[-0.035em] leading-[0.94] text-parchment text-balance">
                  Business, to us, is{" "}
                  <span className="italic text-magenta">a relationship</span>{" "}
                  between two partners.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:col-start-7 lg:pt-6 space-y-6 text-lg text-parchment/80 leading-[1.65] max-w-xl">
              <Reveal delay={0.15}>
                <p className="font-display italic text-2xl md:text-3xl leading-snug text-parchment">
                  Because we have been in this trade for years, we understand
                  your needs and expectations — and we strive to deliver the
                  very best, every single time.
                </p>
              </Reveal>
            </div>
          </div>

          {/* Numbered chapters — editorial grid */}
          <div className="grid md:grid-cols-2 gap-x-16 gap-y-20 border-t border-parchment/15 pt-16 md:pt-20">
            {[
              {
                num: "01",
                title: "Provenance",
                lede: "Every consignment starts at the origin field.",
                body:
                  "We source Timothy, Alfalfa, Meadow and Orchard from certified growers, then screen sunflower and oat lots for moisture, mould and aflatoxin — one origin, one paper trail, no shortcuts.",
              },
              {
                num: "02",
                title: "Slow Craft",
                lede: "Cured on the field. Milled on our floor.",
                body:
                  "Bales are sun-cured to the right dryness, hand-sorted for long strands, and our animal feed premixes are milled fresh in-house at Kamshet — never bulk-repacked from anonymous sacks.",
              },
              {
                num: "03",
                title: "Signed Off",
                lede: "Every formula answers to a nutritionist.",
                body:
                  "Our equine and avian recipes are signed off by veterinarians and nutritionists — 100% natural, preservative-free, steroid-free, and traceable to a batch on a bag.",
              },
              {
                num: "04",
                title: "Delivered",
                lede: "One bale or one container — same insistence.",
                body:
                  "We ship to stables, aviaries, hobby farms, small-pet homes and premium pantries with the same attention. Order in the evening, hear back the next morning.",
              },
            ].map((c, i) => (
              <Reveal key={c.num} delay={(i % 2) * 0.12}>
                <article className="group relative">
                  <div className="flex items-baseline gap-6">
                    <span className="font-display italic font-light text-6xl md:text-7xl text-magenta leading-none tracking-[-0.04em]">
                      {c.num}
                    </span>
                    <div className="h-px flex-1 bg-parchment/20 mb-3" />
                  </div>
                  <div className="mt-6">
                    <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta/80">
                      Chapter {c.num}
                    </div>
                    <h3 className="mt-3 font-display font-light text-4xl md:text-5xl tracking-[-0.03em] leading-[0.98] text-parchment">
                      {c.title}
                    </h3>
                    <p className="mt-4 font-display italic text-xl md:text-2xl text-parchment/90 leading-snug">
                      {c.lede}
                    </p>
                    <p className="mt-5 text-parchment/70 leading-relaxed max-w-md">
                      {c.body}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          {/* Signature line */}
          <Reveal delay={0.2}>
            <div className="mt-24 md:mt-32 flex flex-col md:flex-row items-start md:items-end justify-between gap-6 border-t border-parchment/15 pt-10">
              <div className="font-display italic text-2xl md:text-3xl text-parchment/90 max-w-xl leading-snug">
                &mdash; Every bale carries a name. Every batch, a story.
              </div>
              <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-parchment/60">
                Signed · The RSA Floor
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ————————————————————————————————
          SLIDER · EQUINE FEEDS
         ———————————————————————————————— */}
      <Slider id="equine" {...SLIDERS.equine} />

      {/* ————————————————————————————————
          BRANDS — Four houses
         ———————————————————————————————— */}
      <section
        data-testid="home-brands-section"
        className="relative bg-parchment"
      >
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-36">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-y-12 items-end mb-16 md:mb-20">
              <div className="md:col-span-8">
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4">
                  <span className="h-px w-8 bg-magenta" />
                  The House of RSA
                </div>
                <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl xl:text-8xl mt-8 tracking-[-0.035em] leading-[0.92] text-aubergine text-balance">
                  Four brands.{" "}
                  <span className="italic text-magenta">One insistence</span>{" "}
                  on quality.
                </h2>
              </div>
              <div className="md:col-span-4 md:pl-6">
                <p className="text-ink/70 leading-relaxed">
                  From sport horses and aviary birds to the family pantry —
                  every brand under the RSA family is formulated for the
                  unique biology of its species and the discerning taste of
                  its keeper.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {BRANDS.map((b, i) => (
              <Reveal key={b.slug} delay={(i % 4) * 0.08}>
                <BrandTile brand={b} idx={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————————————————————————————————
          SLIDER · SMALL PETS, BIRDS & PANTRY
         ———————————————————————————————— */}
      <Slider id="species" {...SLIDERS.small} />

      {/* ————————————————————————————————
          TESTIMONIALS
         ———————————————————————————————— */}
      <section className="bg-aubergine text-parchment relative overflow-hidden grain-overlay">
        <div
          aria-hidden
          className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-magenta/20 blur-3xl"
        />
        <div className="relative mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-32">
          <Reveal>
            <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta/90 flex items-center gap-4 mb-6">
              <span className="h-px w-8 bg-magenta/60" />
              Voices We Are Proud Of
            </div>
            <h2 className="font-display font-light text-5xl md:text-6xl lg:text-7xl tracking-[-0.035em] leading-[0.94] text-parchment mb-20 max-w-4xl">
              The kind of{" "}
              <span className="italic text-magenta">quiet loyalty</span> we
              work for.
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-x-14 gap-y-16 border-t border-parchment/15 pt-16">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={(i % 2) * 0.15}>
                <figure className="relative">
                  <span
                    aria-hidden
                    className="absolute -top-10 -left-2 font-display italic text-8xl text-magenta/30 leading-none pointer-events-none select-none"
                  >
                    &ldquo;
                  </span>
                  <blockquote className="relative font-display italic font-light text-2xl md:text-[26px] tracking-[-0.01em] leading-[1.35] text-parchment/95 text-pretty">
                    {t.body}
                  </blockquote>
                  <figcaption className="mt-6 flex items-center gap-3 text-parchment/70">
                    <span className="h-px w-8 bg-magenta" />
                    <span className="font-semibold text-parchment">{t.name}</span>
                    <span className="text-[10px] uppercase tracking-[0.24em] text-parchment/50 font-semibold">
                      {t.role}
                    </span>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ————————————————————————————————
          CTA
         ———————————————————————————————— */}
      <section className="bg-parchment">
        <div className="mx-auto max-w-[1440px] px-6 md:px-12 lg:px-16 py-24 md:py-36">
          <Reveal>
            <div className="grid md:grid-cols-12 gap-y-12 items-end">
              <div className="md:col-span-8">
                <div className="text-[10px] uppercase tracking-[0.32em] font-semibold text-magenta flex items-center gap-4">
                  <span className="h-px w-8 bg-magenta" />
                  Let us talk
                </div>
                <h2 className="font-display font-light text-6xl md:text-7xl lg:text-8xl mt-8 tracking-[-0.045em] leading-[0.9] text-aubergine text-balance">
                  Whether a single bale or{" "}
                  <span className="italic text-magenta">a container</span>,
                  <br />
                  we deliver.
                </h2>
              </div>
              <div className="md:col-span-4 md:pl-6">
                <p className="text-ink/70 leading-relaxed max-w-sm">
                  Send us a note about your herd, your aviary, your pantry or
                  your plan — we respond within one business day with pricing,
                  sample images and shipping options.
                </p>
                <Link
                  to="/contact"
                  data-testid="home-final-cta"
                  className="mt-10 inline-flex items-center gap-3 bg-aubergine hover:bg-magenta text-parchment text-[11px] tracking-[0.28em] uppercase font-semibold px-8 py-5 transition-colors duration-300"
                >
                  Begin a Conversation
                  <span className="h-px w-6 bg-parchment" />
                </Link>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function CommodityCard({ commodity, idx }) {
  const isCircle = idx % 3 === 0;
  const isArch = idx % 3 === 1;
  const shapeClass = isCircle
    ? "rounded-full"
    : isArch
      ? "rounded-t-[50%]"
      : "rounded-b-[50%]";
  return (
    <article className="group">
      <div className={`relative overflow-hidden aspect-[3/4] ${shapeClass}`}>
        <motion.img
          src={commodity.image}
          alt={commodity.name}
          className="absolute inset-0 h-full w-full object-cover"
          initial={{ scale: 1.05 }}
          whileInView={{ scale: 1 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
        />
        <motion.div
          className="absolute inset-0"
          whileHover={{ scale: 1.06 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-aubergine/40 via-transparent to-transparent" />
        </motion.div>
      </div>
      <div className="mt-6 flex items-baseline gap-4">
        <span className="font-display italic font-light text-2xl text-magenta">
          {commodity.tag}
        </span>
        <div className="flex-1">
          <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-aubergine/60">
            {commodity.kind}
          </div>
          <h3 className="font-display text-3xl md:text-4xl tracking-[-0.02em] mt-2 text-aubergine">
            {commodity.name}
          </h3>
        </div>
      </div>
      <p className="mt-4 text-ink/65 leading-relaxed">{commodity.body}</p>
    </article>
  );
}

function BrandTile({ brand, idx }) {
  const isEven = idx % 2 === 0;
  const shape = isEven ? "rounded-t-[50%]" : "rounded-b-[50%]";

  const content = (
    <>
      <div className={`relative aspect-[4/5] overflow-hidden ${shape} bg-parchment ring-1 ring-aubergine/10`}>
        <motion.img
          src={brand.image}
          alt={brand.name}
          className="absolute inset-0 h-full w-full object-cover opacity-0 group-hover:opacity-30 transition-opacity duration-700"
          draggable={false}
        />
        <div className="relative h-full grid place-items-center p-6">
          {brand.logo ? (
            <img
              src={brand.logo}
              alt={`${brand.name} logo`}
              className="max-h-[70%] max-w-[80%] object-contain transition-transform duration-700 group-hover:scale-[1.06]"
              style={{ mixBlendMode: "multiply" }}
              draggable={false}
            />
          ) : (
            <div className="text-center px-4">
              <div className="font-display italic font-light text-4xl md:text-5xl tracking-[-0.03em] bg-gradient-to-br from-magenta via-magenta-deep to-aubergine-light bg-clip-text text-transparent">
                {brand.name}
              </div>
              <div className="mt-3 text-[9px] tracking-[0.32em] uppercase font-semibold text-aubergine/60">
                Premium Pantry
              </div>
            </div>
          )}
        </div>
      </div>
      <div className="mt-6">
        <div className="text-[10px] uppercase tracking-[0.28em] font-semibold text-magenta">
          0{idx + 1} · {brand.species.split(/[&,·]/)[0].trim()}
        </div>
        <div className="mt-3 flex items-baseline gap-2">
          <div className="font-display font-light text-3xl md:text-4xl tracking-[-0.02em] text-aubergine group-hover:text-magenta transition-colors">
            {brand.name}
          </div>
          {brand.website && (
            <ExternalLink className="h-4 w-4 text-aubergine/40 group-hover:text-magenta transition-colors" />
          )}
        </div>
        <div className="mt-1 italic font-serif text-ink/60 text-sm">
          {brand.tagline}
        </div>
      </div>
    </>
  );

  if (brand.website) {
    return (
      <a
        href={brand.website}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`home-brand-card-${brand.slug}`}
        className="group relative block"
      >
        {content}
      </a>
    );
  }
  return (
    <Link
      to={`/brands/${brand.slug}`}
      data-testid={`home-brand-card-${brand.slug}`}
      className="group relative block"
    >
      {content}
    </Link>
  );
}
